import { useTranslation } from 'react-i18next'
import './booking-history.scss'
import { bookingService, stringService } from 'services'
import { useBookingHistory, usePageTitle } from 'hooks'
import BasePage from 'pages/base-page/base-page.page'
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  IonLabel,
  IonSpinner,
  IonToast,
} from '@ionic/react'
import { icon_option } from 'icons'
import { useEffect, useState } from 'react'
import { BaseButton, BaseModal } from 'components'
import { IRequestBookingHistory, IResponseBookingHistory } from 'interfaces'
import { IBollettino, IRequestDeleteBook } from 'interfaces/models.interface'
import i18next from 'i18n'
import { format, parseISO } from 'date-fns'

const LIST_BLOCK_SIZE = 5
const TOAST_DURATION: number = Number(process.env.REACT_APP_TOAST_DURATION) || 5000
const DEFAULT_SEARCH_PARAMS: IRequestBookingHistory = {
  stato: '',
  limit: LIST_BLOCK_SIZE,
  offset: 0,
}

interface IStates {
  [key: string]: string
}
const states: IStates = {
  C1: i18next.t('taking_charge'),
  I: i18next.t('payable'),
  P: i18next.t('paid'),
  A1: i18next.t('cancelled'),
  A2: i18next.t('expired'),
}

const BookingHistory: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isOpenT, setIsOpenT] = useState(false)
  const [message, setMessage] = useState('')
  const [color, setColor] = useState('')
  const [resultsLoaded, setResultsLoaded] = useState(false)
  const [searchParams, setSearchParams] = useState<IRequestBookingHistory>(DEFAULT_SEARCH_PARAMS)
  const [loadMore, setLoadMore] = useState<boolean>(false)
  const [loader, setLoader] = useState<boolean>(false)
  const [items, setItems] = useState<IResponseBookingHistory[]>([])
  const [selectedItem, setSelectedItem] = useState<IResponseBookingHistory | null>(null)
  const { t } = useTranslation()
  const titlePage = stringService.capitalizeFirstLetter(t('booking-history'))
  usePageTitle(titlePage!)
  const bookingHistory = useBookingHistory(searchParams)

  useEffect(() => {
    if (!!bookingHistory && bookingHistory.data.length > 0) {
      setItems([...items, ...bookingHistory.data])
      setResultsLoaded(true)
      setLoadMore(false)
    } else {
      setResultsLoaded(false)
    }
  }, [bookingHistory])

  const onLoadMoreClick = () => {
    setLoadMore(true)
    setSearchParams((sp) => {
      return {
        ...sp,
        limit: LIST_BLOCK_SIZE,
        offset: (sp?.offset || 0) + LIST_BLOCK_SIZE,
      }
    })
  }
  const handleDownloadClick = () => {
    setLoader(true)
    if (selectedItem && selectedItem.idBooking) {
      bookingService.report(
        { id: selectedItem.idBooking },
        (res: IBollettino) => {
          // Decodifica la stringa Base64 e crea un oggetto Blob
          const byteCharacters = atob(res.blob)
          const byteNumbers = new Array(byteCharacters.length)

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }

          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })

          // Crea il link per il download
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.setAttribute('download', res.nome)
          document.body.appendChild(link)

          // Simula il click per avviare il download
          link.click()

          // Pulisce dopo l'uso
          document.body.removeChild(link)
          URL.revokeObjectURL(link.href)
          setLoader(false)
        },
        (error) => {
          console.log(error)
        },
      )
    }
  }
  const deleteBook = (id: IRequestDeleteBook) => {
    bookingService.deleteBook(
      id,
      () => {
        setItems([])
        setSearchParams({ stato: '', limit: LIST_BLOCK_SIZE, offset: 0 })
        setColor('success')
        setMessage(t('delete_book'))
        setIsOpenT(true)
        setIsOpen(false)
      },
      () => {
        setColor('danger')
        setMessage(t('delete_book_error'))
        setIsOpenT(true)
      },
    )
  }
  function formatDate(dateString: string | undefined) {
    if (!!dateString) {
      const dateObj = parseISO(dateString)
      const formattedDate = format(dateObj, 'yyyy-MM-dd')
      return formattedDate
    }
  }
  return (
    <BasePage pageClass="edisu-page-about-us ">
      <div className="container">
        {items.length !== 0 ? (
          items.map((item, index) => {
            return (
              <IonCard className="card" key={index}>
                <div className="firstRowCard">
                  <div className="img_state">
                    {<img className="img" src={item.struttura.immagine} alt="img" />}
                    <IonLabel className={`label color-${item.stato}`}> {states[item.stato]}</IonLabel>
                  </div>
                  <IonIcon
                    onClick={() => {
                      setIsOpen(true)
                      setSelectedItem(item)
                    }}
                    className="icon"
                    icon={icon_option}
                  />
                </div>

                <IonCardHeader>
                  <IonCardTitle>{item.struttura.nome}</IonCardTitle>
                  <IonCardSubtitle>
                    {t('id_booking')}: {item.idBooking}
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonLabel>
                    {t('check_in')}: {formatDate(item.decorrenza)}
                  </IonLabel>
                  <br />
                  <IonLabel>
                    {t('check_out')}: {formatDate(item.cessazione)}
                  </IonLabel>
                  <br />
                  <IonLabel>
                    {t('price')}: {item.totale}€
                  </IonLabel>
                </IonCardContent>
              </IonCard>
            )
          })
        ) : (
          <IonLabel className="ion-padding-top noBook">{t('noBook')}</IonLabel>
        )}
      </div>
      <IonToast
        isOpen={isOpenT}
        color={color}
        onDidDismiss={() => setIsOpenT(false)}
        message={message}
        duration={TOAST_DURATION}
      ></IonToast>
      {resultsLoaded && bookingHistory?.hasMore && (
        <div className="buttonWrapper ion-padding">
          {loadMore ? (
            <IonSpinner name="crescent" color="primary" />
          ) : (
            <BaseButton onClickFunction={() => onLoadMoreClick()} label={t('load_more')}></BaseButton>
          )}
        </div>
      )}

      <BaseModal isOpen={isOpen} setIsOpen={setIsOpen} title={selectedItem?.struttura.nome}>
        <IonCardSubtitle>{selectedItem?.struttura.indirizzo}</IonCardSubtitle>

        <p>
          {t('id_booking')}: {selectedItem?.idBooking}
        </p>
        <p>
          {t('state')}: {states[!!selectedItem?.stato ? selectedItem?.stato : '']}
        </p>
        <p>
          {t('check_in')}: {formatDate(selectedItem?.decorrenza)}
        </p>
        <p>
          {t('check_out')}: {formatDate(selectedItem?.cessazione)}
        </p>
        <p>
          {t('type_room')}: {selectedItem?.prenotazioniCamere[0].tipologiaCamera.descrizione}
        </p>
        <p>
          {t('price')}: {selectedItem?.totale}
        </p>

        <div className="modalButtons">
          <div className={selectedItem?.stato === 'C' || selectedItem?.stato === 'A1' || selectedItem?.stato === 'A2' ? 'hidden' : ''}>
            {loader ? (
              <IonSpinner name="crescent" color="primary" />
            ) : (
              <></>
              //TODO <BaseButton onClickFunction={handleDownloadClick} label={t('download_bulletin')}></BaseButton>
            )}
          </div>

          <br />
          <div className={selectedItem?.stato !== 'C1' ? 'hidden' : ''}>
            <BaseButton
              onClickFunction={() => deleteBook({ id: selectedItem!.idBooking })}
              color="danger"
              label={t('cancel_reservation')}
            ></BaseButton>
          </div>
        </div>
      </BaseModal>
    </BasePage>
  )
}

export default BookingHistory
